import { getHoveredDatapoint } from './xychart-tooltip.js';
import type { Point } from '../../../../core/types/point.js';
import { isBandScale } from '../../../../core/utils/detect-scale.js';
import type { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import type {
  AnyXYChartScale,
  RECT_VARIANT,
  RectDatapointInternal,
  RectSeriesWithDatapoints,
  WeakMapDatapoints,
} from '../../../types/xy-chart-internals.js';
import type { XYAccessorDataTypes } from '../../../types/xy-chart.js';

function isHoveredAreaX(
  xHoveredDomain: XYAccessorDataTypes,
  xScale: AnyXYChartScale,
  datapoint: RectDatapointInternal,
) {
  if (isBandScale(xScale)) {
    return datapoint.x0 === xHoveredDomain;
  } else {
    const { x1 } = datapoint as { x1: XYAccessorDataTypes };
    return (
      (xHoveredDomain >= datapoint.x0 && xHoveredDomain <= x1) ||
      (xHoveredDomain <= datapoint.x0 && xHoveredDomain >= x1)
    );
  }
}

function isHoveredAreaY(
  yHoveredDomain: XYAccessorDataTypes,
  yScale: AnyXYChartScale,
  datapoint: RectDatapointInternal,
) {
  if (isBandScale(yScale)) {
    return datapoint.y0 === yHoveredDomain;
  } else {
    const { y1 } = datapoint as { y1: XYAccessorDataTypes };
    return (
      (yHoveredDomain >= datapoint.y0 && yHoveredDomain <= y1) ||
      (yHoveredDomain <= datapoint.y0 && yHoveredDomain >= y1)
    );
  }
}

export function getHoveredRectDatapoint(
  relativePosition: Point,
  rectSeries: RectSeriesWithDatapoints[],
  axisScaleMap: AxisIdToScales,
  datapointMetadataMap: WeakMapDatapoints,
) {
  for (const series of rectSeries) {
    const datapoint = getHoveredDatapoint<typeof RECT_VARIANT>(
      relativePosition,
      series,
      axisScaleMap,
      datapointMetadataMap,
      (xScale, yScale, datapoint, xHoveredDomain, yHoveredDomain) =>
        isHoveredAreaX(
          xHoveredDomain,
          xScale,
          datapoint as RectDatapointInternal,
        ) &&
        isHoveredAreaY(
          yHoveredDomain,
          yScale,
          datapoint as RectDatapointInternal,
        ),
    );

    if (datapoint) {
      return { datapoint, series };
    }
  }

  return undefined;
}
